import {
  defModule,
  Task,
  Update,
  type UpdateResult,
  type TaskDeclaration,
  type TaskMessage
} from '@st/redux'
import {
  BasicUser,
  BulkCreateChecklistItemsItem,
  FolderListRow,
  FolderTag,
  FolderListState,
  STFolderListExport,
  TaxReturnDeliveryStatus,
  SDKCommand,
  SDKRequestFor
} from '@st/sdk'
import { RETURN_TYPES } from '@st/tax-folder'
import { setToggle } from '@st/util/array-set'
import { inflect } from '@st/util/inflect'
import { isNotEmpty } from '@st/util/json-value'
import { search } from '@st/util/search'
import { direction, levels, rank, sort, SortDirection, SortState, toggleSort } from '@st/util/sort'
import { Effect, Stream } from 'effect'
import { match, P } from '@st/pattern'
import {
  AppFlags,
  BrowserDownloads,
  Routing,
  SdkClient,
  Snackbar,
  currentLocalISODate,
  observeShortcuts,
  query
} from '~/effects'
import { callSdkRequest, observeOrganizationRoute, runSdkRequest } from '~/features/module-runtime'
import { copyTextToClipboard } from '~/features/copy-to-clipboard'
import type { AppRoute } from '~/features/routes'
import { reportHandledError } from '../error-reporting/report-handled-error'
import {
  idleBulkEnroll,
  openBulkEnroll,
  sendEnrollRequestEffect,
  updateBulkEnroll,
  type BulkEnrollInput,
  type BulkEnrollMessage,
  type BulkEnrollState
} from '../client-organizer-reminders/st-enroll-in-reminders-module'
import {
  emptyRequestQueue,
  enqueueRequest as enqueueRequestLedger,
  finalizeSettledQueueByVsn as finalizeSettledQueueByVsnLedger,
  markRequestFailed as markRequestFailedLedger,
  markRequestSettledAtVsn as markRequestSettledAtVsnLedger,
  markRequestStarted as markRequestStartedLedger,
  type RequestQueue
} from '../st-folder-viewer/st-folder-request-queue'
import {
  FolderListSortColumn,
  FolderListStatusFilter,
  STFolderListFilters,
  FOLDER_DEFAULT_SORT,
  FOLDER_LIST_DEFAULT_FILTERS,
  FOLDER_SORT_TRANSITIONS,
  UNASSIGNED
} from './st-folder-list-filters-module'
import {
  idleBulkSendClientOrganizers,
  openBulkSendClientOrganizers,
  sendClientOrganizersEffect,
  updateBulkSendClientOrganizers,
  type BulkSendClientOrganizersInput,
  type BulkSendClientOrganizersMessage,
  type BulkSendClientOrganizersState
} from './st-bulk-send-client-organizers'
type STFolderListModuleStateBase<RouteWorkflow> = {
  nextRequestId: number
  organizationSlug: string | null
  currentUserId: string | null
  selectedFolderIds: string[]
  devMode: boolean
  filters: STFolderListFilters
  sortState: SortState<FolderListSortColumn> | undefined
  dialog: STFolderListDialog | undefined
  routeWorkflow: RouteWorkflow
  acceptedWorkflowWrite: AcceptedWorkflowWrite
  csvExport: CsvExportState
  bulkAddChecklistItemsDocTypes: BulkAddChecklistItemsDocTypesState
}

type STFolderListModuleStateBaseNotLoaded =
  STFolderListModuleStateBase<STFolderListPendingRouteWorkflow>

type STFolderListModuleStateIdle = STFolderListModuleStateBaseNotLoaded & {
  status: 'idle'
  organizationId: undefined
  folderListState: undefined
}

type STFolderListModuleStateLoadingOrganization = STFolderListModuleStateBaseNotLoaded & {
  status: 'loadingOrganization'
  organizationSlug: string
  currentUserId: string
  organizationId: undefined
  folderListState: undefined
}

type STFolderListModuleStateLoading = STFolderListModuleStateBaseNotLoaded & {
  status: 'loading'
  organizationSlug: string
  currentUserId: string
  organizationId: string
  folderListState: undefined
}

type STFolderListModuleStateFailed = STFolderListModuleStateBaseNotLoaded & {
  status: 'failed'
  organizationSlug: string
  currentUserId: string
  organizationId: undefined
  folderListState: undefined
}

type STFolderListModuleStateNotLoaded =
  | STFolderListModuleStateIdle
  | STFolderListModuleStateLoadingOrganization
  | STFolderListModuleStateLoading
  | STFolderListModuleStateFailed

type STFolderListModuleStateLoaded =
  STFolderListModuleStateBase<STFolderListActiveRouteWorkflow> & {
    status: 'loaded'
    organizationSlug: string
    currentUserId: string
    organizationId: string
    folderListState: FolderListState
    stateVsn: number
    requestQueue: RequestQueue<STFolderListQueuedRequest>
  }

type STFolderListModuleState = STFolderListModuleStateNotLoaded | STFolderListModuleStateLoaded

type STFolderListSocketState = STFolderListModuleStateLoading | STFolderListModuleStateLoaded

export type CsvExportState =
  | { status: 'idle' }
  | { status: 'loading'; key: string; folderIds: string[] }
  | {
      status: 'loaded'
      key: string
      folderIds: string[]
      data: STFolderListExport
    }
  | { status: 'failed'; key: string; folderIds: string[] }

export type BulkChecklistDocumentType = { id: string; name: string }

export type BulkAddChecklistItemsDocTypesState =
  | { status: 'idle' }
  | { status: 'loading'; key: string; schemaId: string; folderIds: string[] }
  | {
      status: 'loaded'
      key: string
      schemaId: string
      folderIds: string[]
      documentTypes: BulkChecklistDocumentType[]
    }
  | { status: 'failed'; key: string; schemaId: string | null; folderIds: string[] }

export type STFolderListRouteWorkflowKind = 'sendClientOrganizers' | 'enrollInReminders'

/**
 * Exactly one route-addressed folder-list workflow may be open. Before the folder snapshot loads,
 * the session stores only route intent; a loaded session turns that intent into this active union.
 * Workflow-specific phases live below their variant instead of in parallel fields that must sync.
 */
export type STFolderListRouteWorkflow =
  | { type: 'none' }
  | { type: 'sendClientOrganizers'; send: BulkSendClientOrganizersState }
  | { type: 'enrollInReminders'; enroll: BulkEnrollState }

type STFolderListPendingRouteWorkflow = {
  status: 'pending'
  intent: STFolderListRouteWorkflowKind | null
}

type STFolderListActiveRouteWorkflow = {
  status: 'active'
  workflow: STFolderListRouteWorkflow
}

type AcceptedWorkflowWrite = 'sendClientOrganizers' | 'enrollInReminders' | null

export type BulkSendFolderGroups = {
  folders: FolderListRow[]
  sendable: FolderListRow[]
  missingEmails: FolderListRow[]
}

export type STFolderAction =
  | { type: 'openSendClientOrganizerDialog'; folder: FolderListRow }
  | { type: 'openCopyShareableLinkDialog'; folder: FolderListRow }
  | { type: 'openFolder'; folder: FolderListRow }
  | {
      type: 'openFolderBlockedByLowBalance'
      currentBalance: number
    }
  | { type: 'deleteFolder'; folder: FolderListRow }
  | { type: 'archiveFolder'; folder: FolderListRow }
  | { type: 'unarchiveFolder'; folder: FolderListRow }
  | { type: 'unlockClientOrganizer'; folder: FolderListRow }
  | { type: 'submitClientOrganizer'; folder: FolderListRow; forceSubmit: boolean }
  | { type: 'assignFolder'; folder: FolderListRow; userId: string }
  | { type: 'unassignFolder'; folder: FolderListRow; userId: string }
  | { type: 'editFolderTags'; folder: FolderListRow }
  | { type: 'viewRemindersSchedule'; folder: FolderListRow; reminderSchedule: ReminderSchedule }
  | { type: 'previewQuestionnaire'; folderId: string }
  | { type: 'openCommandPalette' }
  | BatchActionType

export type BatchActionGroup = 'folder' | 'clientOrganizer'

export type BatchActionType =
  | { type: 'archiveFolders'; group: 'folder'; folderIds: string[] }
  | { type: 'unarchiveFolders'; group: 'folder'; folderIds: string[] }
  | { type: 'assignFolders'; group: 'folder'; folderIds: string[] }
  | { type: 'addChecklistItems'; group: 'folder'; folderIds: string[] }
  | { type: 'deleteFolders'; group: 'folder'; folderIds: string[] }
  | { type: 'sendClientOrganizers'; group: 'clientOrganizer'; folderIds: string[] }
  | { type: 'exportCsv'; group: 'clientOrganizer'; folderIds: string[] }
  | { type: 'enrollInReminders'; group: 'clientOrganizer'; folderIds: string[] }
  | { type: 'unenrollFromReminders'; group: 'clientOrganizer'; folderIds: string[] }

type RouteWorkflowAction = Extract<
  STFolderAction,
  { type: 'sendClientOrganizers' | 'enrollInReminders' }
>

type SharedOrganizerDialogAction = Extract<
  STFolderAction,
  { type: 'openSendClientOrganizerDialog' | 'openCopyShareableLinkDialog' }
>

type STFolderListOpenAction = Exclude<STFolderAction, SharedOrganizerDialogAction>

export type STFolderListDialog = {
  action: Exclude<STFolderListOpenAction, RouteWorkflowAction>
  requestId?: string
}

export type ReminderSchedule = NonNullable<FolderListRow['clientOrganizer']['reminderSchedule']>

type CsvExportMessage = { type: 'loaded'; data: STFolderListExport } | { type: 'failed' }

type BulkAddChecklistItemsDocTypesMessage =
  | {
      type: 'loaded'
      documentTypes: BulkChecklistDocumentType[]
    }
  | { type: 'failed' }

const observeFolderListOrganization = observeOrganizationRoute({ organizationSlug: P.string })
type FolderListOrganizationContext = TaskMessage<typeof observeFolderListOrganization>['context']
type ObservedFolderListRoute = Exclude<
  FolderListOrganizationContext,
  { status: 'inactive' }
>['route']

export type STFolderListMessage =
  | TaskMessage<typeof observeFolderListOrganization>
  | {
      type: 'stateLoad'
      vsn: number
      state: FolderListState
    }
  | { type: 'response'; requestId: string }
  | { type: 'requestUnauthorized'; requestId: string }
  | { type: 'requestFailed'; requestId: string }
  | { type: 'showSnackbar'; message: string }
  | { type: 'showErrorSnackbar'; message: string }
  | { type: 'shortcutTriggered'; shortcut: string }
  | { type: 'openDialog'; action: STFolderListOpenAction }
  | { type: 'closeDialog' }
  | { type: 'closeRouteWorkflow' }
  | { type: 'bulkSendClientOrganizers'; message: BulkSendClientOrganizersMessage }
  | { type: 'bulkEnrollInReminders'; message: BulkEnrollMessage }
  | { type: 'requestCsvExport'; folderIds: string[] }
  | { type: 'csvExport'; message: CsvExportMessage }
  | { type: 'downloadCsvExport' }
  | { type: 'bulkAddChecklistItemsDocTypes'; message: BulkAddChecklistItemsDocTypesMessage }
  | { type: 'copyOrganizationId' }
  | {
      type: 'openFolder'
      folderId: string
      lowBalanceCurrentBalance: number | undefined
    }
  | { type: 'previewQuestionnaire'; folderId: string }
  | { type: 'openCreateImport' }
  | { type: 'unarchiveFolder'; folder: FolderListRow }
  | { type: 'unlockClientOrganizer'; folder: FolderListRow }
  | { type: 'submitClientOrganizer'; folder: FolderListRow; forceSubmit: boolean }
  | { type: 'assignFolder'; folderId: string; userId: string }
  | { type: 'unassignFolder'; folderId: string; userId: string }
  | { type: 'updateFolderTags'; folderId: string; addTagIds: string[]; removeTagIds: string[] }
  | { type: 'bulkArchiveFolders'; folderIds: string[] }
  | { type: 'bulkUnarchiveFolders'; folderIds: string[] }
  | { type: 'bulkDeleteFolders'; folderIds: string[] }
  | { type: 'archiveFolder'; folder: FolderListRow }
  | { type: 'deleteFolder'; folder: FolderListRow }
  | {
      type: 'bulkUnenrollFromReminders'
      folderIds: string[]
    }
  | {
      type: 'bulkUpdateFolderAssignees'
      folderIds: string[]
      addAssigneeIds: string[]
      removeAssigneeIds: string[]
    }
  | {
      type: 'bulkCreateChecklistItems'
      folderIds: string[]
      items: BulkCreateChecklistItemsItem[]
    }
  | { type: 'toggleFolderSelected'; folderId: string }
  | { type: 'setSelectedFolderIds'; folderIds: string[] }
  | { type: 'deselectAll' }
  | { type: 'updateFilters'; filters: STFolderListFilters }
  | { type: 'toggleSort'; column: FolderListSortColumn }
  | { type: 'setDevMode'; devMode: boolean }

const observeFolderListTopic = Task.define((_currentUserId: string, organizationId: string) =>
  query({ type: 'folders/getFolderListState', organizationId }).pipe(
    Stream.zipWithIndex,
    Stream.map(
      ([state, index]): STFolderListMessage => ({ type: 'stateLoad', state, vsn: index + 1 })
    ),
    Stream.orDie
  )
)

const observeCommandPaletteShortcut = Task.define(() =>
  Task.pipe(
    observeShortcuts(['$mod+k']),
    Task.map((shortcut): STFolderListMessage => ({ type: 'shortcutTriggered', shortcut }))
  )
)

const loadBulkChecklistDocumentTypes = Task.define(
  (schemaId: string, key: string, ...folderIds: string[]) =>
    loadBulkAddChecklistItemsDocTypesEffect({ schemaId, folderIds, key })
)

const loadCsvExport = Task.define((organizationId: string, key: string, ...folderIds: string[]) =>
  loadCsvExportEffect({ organizationId, folderIds, key })
)

const loadBulkEnrollToday = Task.define(() =>
  Effect.map(
    currentLocalISODate,
    (today): STFolderListMessage => ({
      type: 'bulkEnrollInReminders',
      message: { type: 'opened', today }
    })
  )
)

function sendBulkClientOrganizers(input: BulkSendClientOrganizersInput) {
  return Effect.map(
    sendClientOrganizersEffect(input),
    (message): STFolderListMessage => ({ type: 'bulkSendClientOrganizers', message })
  )
}

function enrollInReminders(input: BulkEnrollInput) {
  return Effect.map(
    sendEnrollRequestEffect(input),
    (message): STFolderListMessage => ({ type: 'bulkEnrollInReminders', message })
  )
}

type HandleResult = UpdateResult<STFolderListModuleState, STFolderListMessage>

function resetFolderListBase(
  prev: STFolderListModuleState,
  organizationSlug: string | null,
  currentUserId: string | null,
  routeIntent: STFolderListRouteWorkflowKind | null
): STFolderListModuleStateBaseNotLoaded {
  return {
    nextRequestId: prev.nextRequestId,
    organizationSlug,
    currentUserId,
    devMode: prev.devMode,
    filters: prev.filters,
    sortState: prev.sortState,
    selectedFolderIds: [],
    dialog: undefined,
    routeWorkflow: pendingRouteWorkflow(routeIntent),
    acceptedWorkflowWrite: prev.acceptedWorkflowWrite,
    csvExport: { status: 'idle' },
    bulkAddChecklistItemsDocTypes: { status: 'idle' }
  }
}

function resetToIdle(
  prev: STFolderListModuleState,
  organizationSlug: string | null,
  currentUserId: string | null,
  routeIntent: STFolderListRouteWorkflowKind | null
): STFolderListModuleStateIdle {
  return {
    ...resetFolderListBase(prev, organizationSlug, currentUserId, routeIntent),
    status: 'idle',
    organizationId: undefined,
    folderListState: undefined
  }
}

function resetToLoadingOrganization(
  prev: STFolderListModuleState,
  organizationSlug: string,
  currentUserId: string,
  routeIntent: STFolderListRouteWorkflowKind | null
): STFolderListModuleStateLoadingOrganization {
  return {
    ...resetFolderListBase(prev, organizationSlug, currentUserId, routeIntent),
    status: 'loadingOrganization',
    organizationSlug,
    currentUserId,
    organizationId: undefined,
    folderListState: undefined
  }
}

function reduceCsvExport(
  state: Extract<CsvExportState, { status: 'loading' }>,
  message: CsvExportMessage
): CsvExportState {
  switch (message.type) {
    case 'loaded':
      return {
        status: 'loaded',
        key: state.key,
        folderIds: state.folderIds,
        data: message.data
      }
    case 'failed':
      return {
        status: 'failed',
        key: state.key,
        folderIds: state.folderIds
      }
  }
}

function handleCsvExportMessage(
  state: STFolderListModuleStateLoaded,
  message: CsvExportMessage
): STFolderListModuleState {
  if (state.csvExport.status != 'loading') {
    throw new Error(`Unexpected csvExport message outside its loading state`)
  }

  return {
    ...state,
    csvExport: reduceCsvExport(state.csvExport, message)
  }
}

function reduceBulkAddChecklistItemsDocTypes(
  state: Extract<BulkAddChecklistItemsDocTypesState, { status: 'loading' }>,
  message: BulkAddChecklistItemsDocTypesMessage
): BulkAddChecklistItemsDocTypesState {
  switch (message.type) {
    case 'loaded':
      return {
        status: 'loaded',
        key: state.key,
        schemaId: state.schemaId,
        folderIds: state.folderIds,
        documentTypes: message.documentTypes
      }
    case 'failed':
      return {
        status: 'failed',
        key: state.key,
        schemaId: state.schemaId,
        folderIds: state.folderIds
      }
  }
}

function emptyRouteWorkflow(): STFolderListRouteWorkflow {
  return { type: 'none' }
}

function pendingRouteWorkflow(
  intent: STFolderListRouteWorkflowKind | null
): STFolderListPendingRouteWorkflow {
  return { status: 'pending', intent }
}

function activeRouteWorkflow(workflow: STFolderListRouteWorkflow): STFolderListActiveRouteWorkflow {
  return { status: 'active', workflow }
}

function routeWorkflowFromKind(
  kind: STFolderListRouteWorkflowKind | null
): STFolderListRouteWorkflow {
  return match(kind)
    .with(null, emptyRouteWorkflow)
    .with('sendClientOrganizers', () => ({
      type: 'sendClientOrganizers' as const,
      send: idleBulkSendClientOrganizers()
    }))
    .with('enrollInReminders', () => ({
      type: 'enrollInReminders' as const,
      enroll: idleBulkEnroll()
    }))
    .exhaustive()
}

function routeWorkflowKindFromRoute(route: AppRoute | null): STFolderListRouteWorkflowKind | null {
  switch (route?.name) {
    case 'folder_list_send_client_organizers':
      return 'sendClientOrganizers'
    case 'folder_list_organizer_reminders':
      return 'enrollInReminders'
    default:
      return null
  }
}

function isFolderListScopedRoute(route: AppRoute | null): boolean {
  return (
    route?.name == 'folder_list' ||
    route?.name == 'folder_list_send_client_organizers' ||
    route?.name == 'folder_list_organizer_reminders'
  )
}

function normalizeBulkSendFolderIds(
  state: STFolderListModuleStateLoaded,
  folderIds: string[]
): string[] {
  const selectedFolders = selFoldersById(state, folderIds)
  const groups = selBulkSendSelectedFolders(folderIds, selectedFolders)
  return [...groups.missingEmails, ...groups.sendable].map((folder) => folder.id)
}

function openBulkSendClientOrganizersWorkflow(
  state: STFolderListModuleStateLoaded,
  folderIds: string[]
): STFolderListModuleStateLoaded {
  return {
    ...state,
    routeWorkflow: activeRouteWorkflow({
      type: 'sendClientOrganizers',
      send: openBulkSendClientOrganizers({
        organizationId: state.organizationId,
        folderIds: normalizeBulkSendFolderIds(state, folderIds)
      })
    })
  }
}

function handleBulkSendClientOrganizersMessage(
  state: STFolderListModuleStateLoaded,
  message: BulkSendClientOrganizersMessage
): HandleResult {
  const settled =
    message.type == 'sendCompleted' ||
    message.type == 'quotaExceeded' ||
    message.type == 'requestFailed'
  if (settled && state.acceptedWorkflowWrite != 'sendClientOrganizers') {
    return unexpectedFolderListMessage(state, { type: 'bulkSendClientOrganizers', message })
  }

  if (state.routeWorkflow.workflow.type != 'sendClientOrganizers') {
    if (settled) return { ...state, acceptedWorkflowWrite: null }
    return unexpectedFolderListMessage(state, { type: 'bulkSendClientOrganizers', message })
  }

  if (settled && state.routeWorkflow.workflow.send.status != 'sending') {
    return { ...state, acceptedWorkflowWrite: null }
  }

  const send = updateBulkSendClientOrganizers(state.routeWorkflow.workflow.send, message)
  if (message.type == 'send') {
    if (send.status != 'sending') return state
    return Update.pipe(
      {
        ...state,
        acceptedWorkflowWrite: 'sendClientOrganizers' as const,
        routeWorkflow: activeRouteWorkflow({ type: 'sendClientOrganizers', send })
      },
      Update.effect(() =>
        sendBulkClientOrganizers({
          organizationId: send.organizationId,
          folderIds: send.sendableFolderIds
        })
      )
    )
  }

  return {
    ...state,
    acceptedWorkflowWrite: settled ? null : state.acceptedWorkflowWrite,
    routeWorkflow: activeRouteWorkflow({
      type: 'sendClientOrganizers',
      send
    })
  }
}

function openEnrollInRemindersWorkflow(
  state: STFolderListModuleStateLoaded,
  folderIds: string[]
): HandleResult {
  const selectedFolderIds = selFoldersById(state, folderIds).map((folder) => folder.id)
  const enroll = openBulkEnroll({
    organizationId: state.organizationId,
    folderIds: selectedFolderIds
  })

  return {
    ...state,
    routeWorkflow: activeRouteWorkflow({ type: 'enrollInReminders', enroll })
  }
}

function handleBulkEnrollInRemindersMessage(
  state: STFolderListModuleStateLoaded,
  message: BulkEnrollMessage
): HandleResult {
  const settled = message.type == 'response' || message.type == 'requestFailed'
  if (settled && state.acceptedWorkflowWrite != 'enrollInReminders') {
    return unexpectedFolderListMessage(state, { type: 'bulkEnrollInReminders', message })
  }

  if (state.routeWorkflow.workflow.type != 'enrollInReminders') {
    if (settled) return { ...state, acceptedWorkflowWrite: null }
    return unexpectedFolderListMessage(state, { type: 'bulkEnrollInReminders', message })
  }

  if (
    settled &&
    (state.routeWorkflow.workflow.enroll.status != 'confirm' ||
      state.routeWorkflow.workflow.enroll.operationStatus != 'enrolling')
  ) {
    return { ...state, acceptedWorkflowWrite: null }
  }

  const enroll = updateBulkEnroll(state.routeWorkflow.workflow.enroll, message)
  if (message.type == 'confirmEnroll') {
    if (enroll.status != 'confirm' || enroll.operationStatus != 'enrolling') return state
    return Update.pipe(
      {
        ...state,
        acceptedWorkflowWrite: 'enrollInReminders' as const,
        routeWorkflow: activeRouteWorkflow({ type: 'enrollInReminders', enroll })
      },
      Update.effect(() =>
        enrollInReminders({
          organizationId: enroll.organizationId,
          folderIds: enroll.folderIds,
          scheduleOptions: enroll.scheduleOptions
        })
      )
    )
  }

  return {
    ...state,
    acceptedWorkflowWrite: settled ? null : state.acceptedWorkflowWrite,
    routeWorkflow: activeRouteWorkflow({
      type: 'enrollInReminders',
      enroll
    })
  }
}

function reduceOpenDialog(
  state: STFolderListModuleStateLoaded,
  action: STFolderListOpenAction
): HandleResult {
  switch (action.type) {
    case 'sendClientOrganizers': {
      const nextState = openBulkSendClientOrganizersWorkflow(state, action.folderIds)

      const organizationSlug = state.organizationSlug
      return Update.pipe(
        nextState,
        Update.effect(() => navigateToRouteWorkflow(organizationSlug, 'sendClientOrganizers'))
      )
    }
    case 'exportCsv': {
      return requestCsvExport({ ...state, dialog: { action } }, action.folderIds)
    }
    case 'enrollInReminders': {
      const nextUpdate = openEnrollInRemindersWorkflow(state, action.folderIds)

      const organizationSlug = state.organizationSlug
      return Update.pipe(
        nextUpdate,
        Update.effect(() => navigateToRouteWorkflow(organizationSlug, 'enrollInReminders'))
      )
    }
    case 'addChecklistItems': {
      const folderIds = action.folderIds
      const schemaIds = new Set(
        folderIds
          .map((folderId) => state.folderListState.folders[folderId]?.schemaId)
          .filter(isNotEmpty)
      )

      const schemaId = schemaIds.size == 1 ? Array.from(schemaIds)[0] : null
      if (!schemaId) {
        return {
          ...state,
          dialog: { action },
          bulkAddChecklistItemsDocTypes: {
            status: 'failed',
            key: 'invalid-schema',
            schemaId: null,
            folderIds
          }
        }
      }

      const key = bulkAddChecklistItemsDocTypesKey(schemaId, folderIds)

      if (
        state.bulkAddChecklistItemsDocTypes.status == 'loading' &&
        state.bulkAddChecklistItemsDocTypes.key == key
      ) {
        return { ...state, dialog: { action } }
      }
      if (
        state.bulkAddChecklistItemsDocTypes.status == 'loaded' &&
        state.bulkAddChecklistItemsDocTypes.key == key
      ) {
        return { ...state, dialog: { action } }
      }

      return {
        ...state,
        dialog: { action },
        bulkAddChecklistItemsDocTypes: { status: 'loading', key, schemaId, folderIds }
      }
    }
    default:
      return { ...state, dialog: { action } }
  }
}

function reduceCloseDialog(state: STFolderListModuleState): HandleResult {
  if (!state.dialog) return state
  return { ...state, dialog: undefined }
}

function reduceCloseRouteWorkflow(state: STFolderListModuleState): HandleResult {
  if (state.status != 'loaded' || state.routeWorkflow.workflow.type == 'none') return state

  const nextState = {
    ...state,
    routeWorkflow: activeRouteWorkflow(emptyRouteWorkflow())
  }
  const organizationSlug = state.organizationSlug
  return Update.pipe(
    nextState,
    Update.effect(() => navigateToFolderList(organizationSlug))
  )
}

function requestCsvExport(state: STFolderListModuleStateLoaded, folderIds: string[]): HandleResult {
  const key = csvExportKey(state.organizationId, folderIds)
  const exportState = state.csvExport

  if (exportState.status == 'loading' && exportState.key == key) {
    return state
  }
  if (exportState.status == 'loaded' && exportState.key == key) {
    return state
  }

  return {
    ...state,
    csvExport: { status: 'loading', key, folderIds }
  }
}

function downloadCsvExport(state: STFolderListModuleStateLoaded): HandleResult {
  if (state.csvExport.status != 'loaded') {
    throw new Error('CSV export is not loaded')
  }
  const csvContent = state.csvExport.data.csvContent
  return [
    state,
    Effect.gen(function* () {
      const downloads = yield* BrowserDownloads
      yield* downloads.save(new Blob([csvContent], { type: 'text/csv' }), 'organizer-links.csv')
      return null
    })
  ] as const
}

function handleViewStateMessage(
  state: STFolderListModuleState,
  message:
    | Extract<STFolderListMessage, { type: 'toggleFolderSelected' }>
    | Extract<STFolderListMessage, { type: 'setSelectedFolderIds' }>
    | Extract<STFolderListMessage, { type: 'deselectAll' }>
    | Extract<STFolderListMessage, { type: 'updateFilters' }>
    | Extract<STFolderListMessage, { type: 'toggleSort' }>
    | Extract<STFolderListMessage, { type: 'setDevMode' }>
): STFolderListModuleState {
  switch (message.type) {
    case 'toggleFolderSelected':
      return { ...state, selectedFolderIds: setToggle(state.selectedFolderIds, message.folderId) }
    case 'setSelectedFolderIds':
      return { ...state, selectedFolderIds: message.folderIds }
    case 'deselectAll':
      return { ...state, selectedFolderIds: [] }
    case 'updateFilters':
      return { ...state, filters: message.filters }
    case 'toggleSort':
      return {
        ...state,
        sortState:
          toggleSort(state.sortState, message.column, FOLDER_SORT_TRANSITIONS) ??
          FOLDER_DEFAULT_SORT
      }
    case 'setDevMode':
      if (state.devMode == message.devMode) return state
      return { ...state, devMode: message.devMode }
  }
}

function handleRequestLifecycleMessage(
  state: STFolderListModuleStateLoaded,
  message:
    | Extract<STFolderListMessage, { type: 'response' }>
    | Extract<STFolderListMessage, { type: 'requestUnauthorized' }>
    | Extract<STFolderListMessage, { type: 'requestFailed' }>
): HandleResult {
  switch (message.type) {
    case 'response': {
      const [nextState, effect] = finishRequest(state, message.requestId, true)
      return effect ? [nextState, effect] : nextState
    }
    case 'requestUnauthorized':
    case 'requestFailed':
      return finishRequest(state, message.requestId, false)[0]
  }
}

function handleFolderListStateLoad(
  state: STFolderListSocketState,
  message: Extract<STFolderListMessage, { type: 'stateLoad' }>
): STFolderListModuleStateLoaded {
  let routeWorkflow: STFolderListActiveRouteWorkflow
  let requestQueue: RequestQueue<STFolderListQueuedRequest>
  if (state.status == 'loading') {
    routeWorkflow = activeRouteWorkflow(routeWorkflowFromKind(state.routeWorkflow.intent))
    requestQueue = emptyRequestQueue<STFolderListQueuedRequest>()
  } else {
    routeWorkflow = state.routeWorkflow
    requestQueue = finalizeSettledQueueByVsnLedger(state.requestQueue, message.vsn)
  }

  return {
    ...state,
    status: 'loaded',
    organizationSlug: state.organizationSlug,
    currentUserId: state.currentUserId,
    organizationId: state.organizationId,
    routeWorkflow,
    folderListState: message.state,
    stateVsn: message.vsn,
    requestQueue,
    // Folders deleted or no longer visible drop out of the selection.
    selectedFolderIds: state.selectedFolderIds.filter(
      (id) => message.state.folders[id] != undefined
    )
  }
}

function handleRequestMessage(
  state: STFolderListModuleStateLoaded,
  message:
    | Extract<STFolderListMessage, { type: 'unarchiveFolder' }>
    | Extract<STFolderListMessage, { type: 'unlockClientOrganizer' }>
    | Extract<STFolderListMessage, { type: 'submitClientOrganizer' }>
    | Extract<STFolderListMessage, { type: 'assignFolder' }>
    | Extract<STFolderListMessage, { type: 'unassignFolder' }>
    | Extract<STFolderListMessage, { type: 'updateFolderTags' }>
    | Extract<STFolderListMessage, { type: 'bulkArchiveFolders' }>
    | Extract<STFolderListMessage, { type: 'bulkUnarchiveFolders' }>
    | Extract<STFolderListMessage, { type: 'bulkDeleteFolders' }>
    | Extract<STFolderListMessage, { type: 'archiveFolder' }>
    | Extract<STFolderListMessage, { type: 'deleteFolder' }>
    | Extract<STFolderListMessage, { type: 'bulkUnenrollFromReminders' }>
    | Extract<STFolderListMessage, { type: 'bulkUpdateFolderAssignees' }>
    | Extract<STFolderListMessage, { type: 'bulkCreateChecklistItems' }>
): HandleResult {
  switch (message.type) {
    case 'unarchiveFolder':
      return startSingleFolderRequest(
        state,
        message.folder,
        'unarchiveFolder',
        'folders/unarchiveFolder',
        'Unarchived'
      )
    case 'unlockClientOrganizer':
      return startRequest(
        state,
        { type: 'unlockClientOrganizer', folderId: message.folder.id },
        { type: 'folders/unlockClientOrganizer', folderId: message.folder.id }
      )
    case 'submitClientOrganizer':
      return startRequest(
        state,
        {
          type: 'submitClientOrganizer',
          folderId: message.folder.id,
          forceSubmit: message.forceSubmit
        },
        {
          type: 'folders/submitClientOrganizer',
          folderId: message.folder.id,
          forceSubmit: message.forceSubmit
        }
      )
    case 'assignFolder':
      if (selHasAssignmentConflict(state, [message.folderId], [message.userId]))
        return [state, showErrorSnackbar('Remove their client access before assigning them.')]
      return startRequest(
        state,
        { type: 'assignFolder', folderId: message.folderId, userId: message.userId },
        {
          type: 'folders/assignUserToFolder',
          folderId: message.folderId,
          userId: message.userId
        }
      )
    case 'unassignFolder':
      return startRequest(
        state,
        { type: 'unassignFolder', folderId: message.folderId, userId: message.userId },
        {
          type: 'folders/unassignUserFromFolder',
          folderId: message.folderId,
          userId: message.userId
        }
      )
    case 'updateFolderTags':
      return startRequest(
        state,
        { type: 'updateFolderTags', folderId: message.folderId },
        {
          type: 'folders/updateFolderTags',
          folderId: message.folderId,
          addTagIds: message.addTagIds,
          removeTagIds: message.removeTagIds
        }
      )
    case 'bulkArchiveFolders':
      return startBulkBinderRequest(state, {
        requestType: 'bulkArchiveFolders',
        commandType: 'folders/bulkArchiveFolders',
        folderIds: message.folderIds,
        verb: 'Archived'
      })
    case 'bulkUnarchiveFolders':
      return startBulkBinderRequest(state, {
        requestType: 'bulkUnarchiveFolders',
        commandType: 'folders/bulkUnarchiveFolders',
        folderIds: message.folderIds,
        verb: 'Unarchived'
      })
    case 'bulkDeleteFolders':
      return startBulkDeleteFolders(state, message.folderIds)
    case 'archiveFolder':
      return startSingleFolderRequest(
        state,
        message.folder,
        'archiveFolder',
        'folders/archiveFolder',
        'Archived'
      )
    case 'deleteFolder':
      return startSingleFolderRequest(
        state,
        message.folder,
        'deleteFolder',
        'folders/deleteFolder',
        'Deleted'
      )
    case 'bulkUnenrollFromReminders':
      return startBulkUnenrollFromReminders(state, message.folderIds)
    case 'bulkUpdateFolderAssignees': {
      if (selHasAssignmentConflict(state, message.folderIds, message.addAssigneeIds))
        return [state, showErrorSnackbar('Remove their client access before assigning them.')]
      const successMessage = `Updated assignees for ${message.folderIds.length} ${inflect(
        message.folderIds.length,
        'binder'
      )}`
      return startRequest(
        state,
        { type: 'bulkUpdateFolderAssignees', folderIds: message.folderIds },
        {
          type: 'folders/bulkUpdateFolderAssignees',
          organizationId: state.organizationId,
          folderIds: message.folderIds,
          addAssigneeIds: message.addAssigneeIds,
          removeAssigneeIds: message.removeAssigneeIds
        },
        successMessage
      )
    }
    case 'bulkCreateChecklistItems': {
      const successMessage = `Added checklist items to ${message.folderIds.length} ${inflect(
        message.folderIds.length,
        'binder'
      )}`
      return startRequest(
        state,
        { type: 'bulkCreateChecklistItems', folderIds: message.folderIds },
        {
          type: 'folders/bulkCreateChecklistItems',
          organizationId: state.organizationId,
          folderIds: message.folderIds,
          items: message.items,
          onDuplicate: 'skip'
        },
        successMessage
      )
    }
  }
}

type RequestId =
  | { type: 'unlockClientOrganizer'; folderId: string }
  | { type: 'submitClientOrganizer'; folderId: string; forceSubmit: boolean }
  | { type: 'assignFolder'; folderId: string; userId: string }
  | { type: 'unassignFolder'; folderId: string; userId: string }
  | { type: 'updateFolderTags'; folderId: string }
  | { type: 'archiveFolder'; folderId: string }
  | { type: 'unarchiveFolder'; folderId: string }
  | { type: 'deleteFolder'; folderId: string }
  | { type: 'bulkArchiveFolders'; folderIds: string[] }
  | { type: 'bulkUnarchiveFolders'; folderIds: string[] }
  | { type: 'bulkDeleteFolders'; folderIds: string[] }
  | { type: 'bulkUnenrollFromReminders'; folderIds: string[] }
  | { type: 'bulkUpdateFolderAssignees'; folderIds: string[] }
  | { type: 'bulkCreateChecklistItems'; folderIds: string[] }

type STFolderListQueuedRequest = {
  type: 'st-folder-list/queuedRequest'
  request: RequestId
  successMessage?: string
}

export const stFolderListModule = defModule<{
  state: STFolderListModuleState
  message: STFolderListMessage
}>({
  init: () => {
    const state = {
      status: 'idle',
      nextRequestId: 1,
      organizationSlug: null,
      currentUserId: null,
      organizationId: undefined,
      devMode: false,
      filters: { ...FOLDER_LIST_DEFAULT_FILTERS },
      sortState: FOLDER_DEFAULT_SORT,
      selectedFolderIds: [],
      folderListState: undefined,
      dialog: undefined,
      routeWorkflow: pendingRouteWorkflow(null),
      acceptedWorkflowWrite: null,
      csvExport: { status: 'idle' },
      bulkAddChecklistItemsDocTypes: { status: 'idle' }
    } satisfies STFolderListModuleState

    return [
      state,
      Effect.gen(function* () {
        const flags = yield* AppFlags
        return { type: 'setDevMode', devMode: flags.devMode } as const
      })
    ] as const
  },
  tasks: (state) => [
    observeFolderListOrganization(),
    ...folderListTopicTasks(state),
    ...commandPaletteSubscription(state),
    ...folderListResourceTasks(state)
  ],
  update: Update.ignoreWhen<STFolderListModuleState, STFolderListMessage>(
    isRepeatedWorkflowWriteIntent
  )(handleSessionMessage)
})

function isRepeatedWorkflowWriteIntent({
  state,
  message
}: Readonly<{ state: STFolderListModuleState; message: STFolderListMessage }>): boolean {
  if (state.acceptedWorkflowWrite == null) return false

  return (
    (message.type == 'bulkSendClientOrganizers' && message.message.type == 'send') ||
    (message.type == 'bulkEnrollInReminders' && message.message.type == 'confirmEnroll')
  )
}

function handleSessionMessage(
  state: STFolderListModuleState,
  message: STFolderListMessage
): HandleResult {
  return match({ state, message })
    .returnType<HandleResult>()
    .with({ message: { type: 'organizationRouteChanged' } }, ({ state, message }) => {
      const route = match(message.context)
        .returnType<ObservedFolderListRoute | null>()
        .with({ status: 'inactive' }, () => null)
        .otherwise((context) => context.route)

      return Update.pipe(reduceRouteChanged(state, route), (state) =>
        reduceOrganizationContextChanged(state, message.context)
      )
    })
    .with(
      { state: { status: 'loaded' }, message: { type: 'shortcutTriggered' } },
      ({ state, message }) => {
        if (message.shortcut != '$mod+k') return state
        return { ...state, dialog: { action: { type: 'openCommandPalette' } } }
      }
    )
    .with({ state: { status: 'loaded' }, message: { type: 'openDialog' } }, ({ state, message }) =>
      reduceOpenDialog(state, message.action)
    )
    .with({ message: { type: 'closeDialog' } }, ({ state }) => reduceCloseDialog(state))
    .with({ message: { type: 'closeRouteWorkflow' } }, ({ state }) =>
      reduceCloseRouteWorkflow(state)
    )
    .with(
      {
        state: { status: 'loaded' },
        message: { type: 'requestCsvExport' }
      },
      ({ state, message }) => requestCsvExport(state, message.folderIds)
    )
    .with(
      {
        state: {
          status: 'loaded',
          csvExport: { status: 'loading' }
        },
        message: { type: 'csvExport' }
      },
      ({ state, message }) => handleCsvExportMessage(state, message.message)
    )
    .with(
      {
        state: {
          status: 'loaded',
          csvExport: { status: 'loaded' }
        },
        message: { type: 'downloadCsvExport' }
      },
      ({ state }) => downloadCsvExport(state)
    )
    .with(
      {
        message: {
          type: P.union('requestCsvExport', 'downloadCsvExport')
        }
      },
      ({ state }) => state
    )
    .with(
      {
        state: { status: 'loaded', bulkAddChecklistItemsDocTypes: { status: 'loading' } },
        message: { type: 'bulkAddChecklistItemsDocTypes' }
      },
      ({ state, message }) => ({
        ...state,
        bulkAddChecklistItemsDocTypes: reduceBulkAddChecklistItemsDocTypes(
          state.bulkAddChecklistItemsDocTypes,
          message.message
        )
      })
    )
    .with({ state: { status: 'loaded' }, message: { type: 'copyOrganizationId' } }, ({ state }) => [
      state,
      copyTextToClipboard(state.organizationId, 'Copied organization ID to clipboard')
    ])
    .with({ message: { type: 'showSnackbar' } }, ({ state, message }) => [
      state,
      showSnackbar(message.message)
    ])
    .with({ message: { type: 'showErrorSnackbar' } }, ({ state, message }) => [
      state,
      showErrorSnackbar(message.message)
    ])
    .with(
      { state: { status: 'loaded' }, message: { type: 'openFolder' } },
      ({ state, message }) => {
        if (message.lowBalanceCurrentBalance != undefined) {
          return {
            ...state,
            dialog: {
              action: {
                type: 'openFolderBlockedByLowBalance',
                currentBalance: message.lowBalanceCurrentBalance
              }
            }
          }
        }
        return [state, navigateToFolder(state.organizationSlug, message.folderId)]
      }
    )
    .with(
      { state: { status: 'loaded' }, message: { type: 'previewQuestionnaire' } },
      ({ state, message }) => [
        state,
        openQuestionnairePreview(state.organizationSlug, message.folderId)
      ]
    )
    .with({ state: { status: 'loaded' }, message: { type: 'openCreateImport' } }, ({ state }) => [
      state,
      openCreateImport(state.organizationSlug)
    ])
    .with(
      {
        message: {
          type: P.union(
            'toggleFolderSelected',
            'setSelectedFolderIds',
            'deselectAll',
            'updateFilters',
            'toggleSort',
            'setDevMode'
          )
        }
      },
      ({ state, message }) => handleViewStateMessage(state, message)
    )
    .with(
      {
        state: { status: P.union('loading', 'loaded') },
        message: { type: 'stateLoad' }
      },
      ({ state, message }) => handleFolderListStateLoad(state, message)
    )
    .with(
      {
        state: { status: 'loaded' },
        message: { type: P.union('response', 'requestUnauthorized', 'requestFailed') }
      },
      ({ state, message }) => handleRequestLifecycleMessage(state, message)
    )
    .with(
      { state: { status: 'loaded' }, message: { type: 'bulkSendClientOrganizers' } },
      ({ state, message }) => handleBulkSendClientOrganizersMessage(state, message.message)
    )
    .with(
      {
        state: {
          status: 'loaded',
          routeWorkflow: {
            workflow: { type: 'enrollInReminders', enroll: { status: 'opening' } }
          }
        },
        message: { type: 'bulkEnrollInReminders', message: { type: 'opened' } }
      },
      ({ state, message }) => handleBulkEnrollInRemindersMessage(state, message.message)
    )
    .with(
      { message: { type: 'bulkEnrollInReminders', message: { type: 'opened' } } },
      ({ state, message }) => unexpectedFolderListMessage(state, message)
    )
    .with(
      { state: { status: 'loaded' }, message: { type: 'bulkEnrollInReminders' } },
      ({ state, message }) => handleBulkEnrollInRemindersMessage(state, message.message)
    )
    .with(
      {
        state: { acceptedWorkflowWrite: 'sendClientOrganizers' },
        message: {
          type: 'bulkSendClientOrganizers',
          message: { type: P.union('sendCompleted', 'quotaExceeded', 'requestFailed') }
        }
      },
      ({ state }) => ({ ...state, acceptedWorkflowWrite: null })
    )
    .with(
      {
        state: { acceptedWorkflowWrite: 'enrollInReminders' },
        message: {
          type: 'bulkEnrollInReminders',
          message: { type: P.union('response', 'requestFailed') }
        }
      },
      ({ state }) => ({ ...state, acceptedWorkflowWrite: null })
    )
    .with(
      {
        state: { status: 'loaded' },
        message: {
          type: P.union(
            'unarchiveFolder',
            'unlockClientOrganizer',
            'submitClientOrganizer',
            'assignFolder',
            'unassignFolder',
            'updateFolderTags',
            'bulkArchiveFolders',
            'bulkUnarchiveFolders',
            'bulkDeleteFolders',
            'archiveFolder',
            'deleteFolder',
            'bulkUnenrollFromReminders',
            'bulkUpdateFolderAssignees',
            'bulkCreateChecklistItems'
          )
        }
      },
      ({ state, message }) => handleRequestMessage(state, message)
    )
    .with(
      {
        message: {
          type: P.union('csvExport', 'bulkAddChecklistItemsDocTypes')
        }
      },
      ({ state, message }) => {
        throw new Error(`Unexpected ${message.type} message in ${state.status} folder-list state`)
      }
    )
    .otherwise(({ state, message }) => unexpectedFolderListMessage(state, message))
}

function unexpectedFolderListMessage(
  state: STFolderListModuleState,
  message: STFolderListMessage
): never {
  throw new Error(`Unexpected folder-list message ${message.type} while ${state.status}`)
}

function reduceOrganizationContextChanged(
  state: STFolderListModuleState,
  context: FolderListOrganizationContext
): HandleResult {
  const routeIntent = routeIntentFromState(state)
  if (context.status == 'inactive') {
    return resetToIdle(state, null, null, routeIntent)
  }

  const organizationSlug = context.route.organizationSlug
  if (!context.currentUserId) {
    return resetToIdle(state, organizationSlug, context.currentUserId, routeIntent)
  }

  if (context.status == 'loading') {
    return resetToLoadingOrganization(state, organizationSlug, context.currentUserId, routeIntent)
  }

  if (context.status == 'loadFailed' || context.status == 'unavailable') {
    return {
      ...resetFolderListBase(state, organizationSlug, context.currentUserId, routeIntent),
      status: 'failed',
      organizationSlug,
      currentUserId: context.currentUserId,
      organizationId: undefined,
      folderListState: undefined
    }
  }

  if (
    (state.status == 'loading' || state.status == 'loaded') &&
    state.organizationSlug == organizationSlug &&
    state.currentUserId == context.currentUserId &&
    state.organizationId == context.organization.id
  ) {
    return state
  }

  return {
    ...resetFolderListBase(state, organizationSlug, context.currentUserId, routeIntent),
    status: 'loading',
    organizationSlug,
    currentUserId: context.currentUserId,
    organizationId: context.organization.id,
    folderListState: undefined
  }
}

function routeIntentFromState(
  state: STFolderListModuleState
): STFolderListRouteWorkflowKind | null {
  if (state.status != 'loaded') return state.routeWorkflow.intent
  if (state.routeWorkflow.workflow.type == 'none') return null
  return state.routeWorkflow.workflow.type
}

function reduceRouteChanged(
  state: STFolderListModuleState,
  route: ObservedFolderListRoute | null
): HandleResult {
  const organizationSlug = route?.organizationSlug ?? null
  const routeWorkflowKind = routeWorkflowKindFromRoute(route)
  if (state.organizationSlug == organizationSlug) {
    if (state.status != 'loaded') {
      let dialog = state.dialog
      if (!isFolderListScopedRoute(route)) dialog = undefined
      if (state.routeWorkflow.intent == routeWorkflowKind && dialog == state.dialog) {
        return state
      }
      return {
        ...state,
        dialog,
        routeWorkflow: pendingRouteWorkflow(routeWorkflowKind)
      }
    }

    if (!isFolderListScopedRoute(route)) {
      if (!state.dialog && state.routeWorkflow.workflow.type == 'none') {
        return state
      }
      return {
        ...state,
        dialog: undefined,
        routeWorkflow: activeRouteWorkflow(emptyRouteWorkflow())
      }
    }

    if (!routeWorkflowKind) {
      if (state.routeWorkflow.workflow.type == 'none') {
        return state
      }
      return {
        ...state,
        routeWorkflow: activeRouteWorkflow(emptyRouteWorkflow())
      }
    }

    let nextState = state
    if (state.routeWorkflow.workflow.type != routeWorkflowKind) {
      nextState = {
        ...state,
        routeWorkflow: activeRouteWorkflow(routeWorkflowFromKind(routeWorkflowKind))
      }
    }
    return nextState
  }

  return resetToIdle(state, organizationSlug, state.currentUserId, routeWorkflowKind)
}

export function selIsArchivingFolder(state: STFolderListModuleState, folderId: string) {
  return hasRequest(state, { type: 'archiveFolder', folderId })
}

export function selIsDeletingFolder(state: STFolderListModuleState, folderId: string) {
  return hasRequest(state, { type: 'deleteFolder', folderId })
}

export function selIsBulkArchiving(state: STFolderListModuleState, folderIds: string[]) {
  return hasRequest(state, { type: 'bulkArchiveFolders', folderIds })
}

export function selIsBulkUnarchiving(state: STFolderListModuleState, folderIds: string[]) {
  return hasRequest(state, { type: 'bulkUnarchiveFolders', folderIds })
}

export function selIsBulkDeleting(state: STFolderListModuleState, folderIds: string[]) {
  return hasRequest(state, { type: 'bulkDeleteFolders', folderIds })
}

export function selIsBulkUnenrolling(state: STFolderListModuleState, folderIds: string[]) {
  return hasRequest(state, { type: 'bulkUnenrollFromReminders', folderIds })
}

export function selIsBulkUpdatingAssignees(state: STFolderListModuleState, folderIds: string[]) {
  return hasRequest(state, { type: 'bulkUpdateFolderAssignees', folderIds })
}

export function selIsBulkAddingChecklistItems(state: STFolderListModuleState, folderIds: string[]) {
  return hasRequest(state, { type: 'bulkCreateChecklistItems', folderIds })
}

function folderListTopicTasks(
  state: STFolderListModuleState
): TaskDeclaration<STFolderListMessage>[] {
  if (state.status != 'loading' && state.status != 'loaded') {
    return []
  }

  return [observeFolderListTopic(state.currentUserId, state.organizationId)]
}

function folderListResourceTasks(
  state: STFolderListModuleState
): TaskDeclaration<STFolderListMessage>[] {
  if (state.status != 'loaded') return []

  const tasks: TaskDeclaration<STFolderListMessage>[] = []
  const routeWorkflow = state.routeWorkflow.workflow
  if (state.csvExport.status == 'loading') {
    tasks.push(
      loadCsvExport(state.organizationId, state.csvExport.key, ...state.csvExport.folderIds)
    )
  }

  const checklist = state.bulkAddChecklistItemsDocTypes
  if (checklist.status == 'loading') {
    tasks.push(
      loadBulkChecklistDocumentTypes(checklist.schemaId, checklist.key, ...checklist.folderIds)
    )
  }

  if (routeWorkflow.type == 'enrollInReminders' && routeWorkflow.enroll.status == 'opening') {
    tasks.push(loadBulkEnrollToday())
  }

  return tasks
}

function commandPaletteSubscription(
  state: STFolderListModuleState
): TaskDeclaration<STFolderListMessage>[] {
  if (!state.devMode) {
    return []
  }
  return [observeCommandPaletteShortcut()]
}

function showSnackbar(message: string) {
  return Effect.gen(function* () {
    const snackbar = yield* Snackbar
    yield* snackbar.show(message)
    return null
  })
}

function showErrorSnackbar(message: string) {
  return Effect.gen(function* () {
    const snackbar = yield* Snackbar
    yield* snackbar.show(message, { variant: 'error' })
    return null
  })
}

function navigateToFolder(organizationSlug: string, folderId: string) {
  return Effect.gen(function* () {
    const routing = yield* Routing
    yield* routing.navigate({
      name: 'organization_folder',
      organizationSlug,
      folderId
    })
    return null
  })
}

function folderLabel(folder: FolderListRow) {
  return folder.name
}

function openQuestionnairePreview(organizationSlug: string, folderId: string) {
  return Effect.gen(function* () {
    const routing = yield* Routing
    yield* routing.openRoute({
      name: 'organization_preview_questionnaire',
      organizationSlug,
      folderId
    })
    return null
  })
}

function openCreateImport(organizationSlug: string) {
  return Effect.gen(function* () {
    const routing = yield* Routing
    yield* routing.navigate({
      name: 'create_import',
      organizationSlug
    })
    return null
  })
}

function navigateToRouteWorkflow(organizationSlug: string, kind: STFolderListRouteWorkflowKind) {
  return Effect.gen(function* () {
    const routing = yield* Routing
    yield* match(kind)
      .with('sendClientOrganizers', () =>
        routing.navigate({
          name: 'folder_list_send_client_organizers',
          organizationSlug
        })
      )
      .with('enrollInReminders', () =>
        routing.navigate({
          name: 'folder_list_organizer_reminders',
          organizationSlug
        })
      )
      .exhaustive()
    return null
  })
}

function navigateToFolderList(organizationSlug: string) {
  return Effect.gen(function* () {
    const routing = yield* Routing
    yield* routing.navigate({
      name: 'folder_list',
      organizationSlug
    })
    return null
  })
}

type BulkFolderRequestType = 'bulkArchiveFolders' | 'bulkUnarchiveFolders'
type BulkFolderCommandType = 'folders/bulkArchiveFolders' | 'folders/bulkUnarchiveFolders'

type SingleFolderRequestType = 'archiveFolder' | 'unarchiveFolder' | 'deleteFolder'
type SingleFolderCommandType =
  | 'folders/archiveFolder'
  | 'folders/unarchiveFolder'
  | 'folders/deleteFolder'

function bulkMessage(count: number, noun: string, verb: string) {
  return `${verb} ${count} ${inflect(count, noun)}`
}

function startSingleFolderRequest(
  state: STFolderListModuleStateLoaded,
  folder: FolderListRow,
  requestType: SingleFolderRequestType,
  commandType: SingleFolderCommandType,
  verb: string
): [
  STFolderListModuleStateLoaded,
  Effect.Effect<STFolderListMessage | STFolderListMessage[] | null, never, unknown>
] {
  const message = `${verb} binder for ${folderLabel(folder)}`
  return startRequest(
    state,
    { type: requestType, folderId: folder.id },
    { type: commandType, folderId: folder.id },
    message
  )
}

function startBulkBinderRequest(
  state: STFolderListModuleStateLoaded,
  args: {
    requestType: BulkFolderRequestType
    commandType: BulkFolderCommandType
    folderIds: string[]
    verb: string
  }
): [
  STFolderListModuleStateLoaded,
  Effect.Effect<STFolderListMessage | STFolderListMessage[] | null, never, unknown>
] {
  const message = bulkMessage(args.folderIds.length, 'binder', args.verb)
  return startRequest(
    state,
    { type: args.requestType, folderIds: args.folderIds },
    {
      type: args.commandType,
      organizationId: state.organizationId,
      folderIds: args.folderIds
    },
    message
  )
}

function startBulkDeleteFolders(
  state: STFolderListModuleStateLoaded,
  folderIds: string[]
): [
  STFolderListModuleStateLoaded,
  Effect.Effect<STFolderListMessage | STFolderListMessage[] | null, never, unknown>
] {
  const message = bulkMessage(folderIds.length, 'binder', 'Deleted')
  return startRequest(
    state,
    { type: 'bulkDeleteFolders', folderIds },
    folderIds.map((folderId) => ({
      type: 'folders/deleteFolder',
      folderId: folderId
    })),
    message
  )
}

function startBulkUnenrollFromReminders(
  state: STFolderListModuleStateLoaded,
  folderIds: string[]
): [
  STFolderListModuleStateLoaded,
  Effect.Effect<STFolderListMessage | STFolderListMessage[] | null, never, unknown>
] {
  const message = `${bulkMessage(
    folderIds.length,
    'client',
    'Unenrolled'
  )} from automated reminders`
  return startRequest(
    state,
    { type: 'bulkUnenrollFromReminders', folderIds },
    {
      type: 'folders/bulkUnenrollClientOrganizersFromReminders',
      organizationId: state.organizationId,
      folderIds: folderIds
    },
    message
  )
}

function requestKey(request: RequestId): string {
  switch (request.type) {
    case 'assignFolder':
    case 'unassignFolder':
      return `${request.type}:${request.folderId}:${request.userId}`
    case 'updateFolderTags':
      return `${request.type}:${request.folderId}`
    case 'archiveFolder':
    case 'unarchiveFolder':
    case 'deleteFolder':
    case 'unlockClientOrganizer':
    case 'submitClientOrganizer':
      return `${request.type}:${request.folderId}`
    case 'bulkArchiveFolders':
    case 'bulkUnarchiveFolders':
    case 'bulkDeleteFolders':
    case 'bulkUnenrollFromReminders':
    case 'bulkUpdateFolderAssignees':
    case 'bulkCreateChecklistItems':
      return `${request.type}:${stableIdsKey(request.folderIds)}`
  }
}

function stableIdsKey(ids: string[]) {
  return ids.slice().sort().join(',')
}

function startRequest(
  state: STFolderListModuleStateLoaded,
  id: RequestId,
  command: SDKCommand | SDKCommand[],
  successMessage?: string
): [
  STFolderListModuleStateLoaded,
  Effect.Effect<STFolderListMessage | STFolderListMessage[] | null, never, unknown>
] {
  const key = requestKey(id)
  if (hasRequestKey(state, key)) {
    return [state, Effect.succeed(null)]
  }

  const queuedRequest: STFolderListQueuedRequest = {
    type: 'st-folder-list/queuedRequest',
    request: id,
    successMessage
  }
  const requestId = state.nextRequestId.toString()
  let requestQueue = enqueueRequestLedger(state.requestQueue, requestId, queuedRequest)
  requestQueue = markRequestStartedLedger(requestQueue, requestId)

  return [
    {
      ...state,
      nextRequestId: state.nextRequestId + 1,
      requestQueue,
      dialog: state.dialog ? { ...state.dialog, requestId } : state.dialog
    },
    Array.isArray(command) ? callBatchSdk(command, requestId) : callSdk(command, requestId)
  ]
}

function hasRequest(state: STFolderListModuleState, id: RequestId) {
  if (state.status != 'loaded') return false
  return hasRequestKey(state, requestKey(id))
}

function hasRequestKey(state: STFolderListModuleStateLoaded, key: string) {
  for (const queueItem of state.requestQueue.items) {
    if (queueItem.status == 'finalized') {
      continue
    }
    if (requestKey(queueItem.op.request.request) == key) return true
  }
  return false
}

function finishRequest(
  state: STFolderListModuleStateLoaded,
  requestId: string,
  showSuccessMessage = false
): [
  STFolderListModuleStateLoaded,
  Effect.Effect<STFolderListMessage | null, never, unknown> | null
] {
  const queueItem = state.requestQueue.items.find((entry) => entry.id == requestId)
  if (!queueItem) return [state, null]
  const successMessage = queueItem.op.request.successMessage
  const shouldShow = showSuccessMessage && successMessage
  const requestQueue = showSuccessMessage
    ? markRequestSettledAtVsnLedger(state.requestQueue, requestId, state.stateVsn)
    : markRequestFailedLedger(state.requestQueue, requestId)

  const shouldCloseDialog = showSuccessMessage && state.dialog?.requestId == requestId
  const nextState = {
    ...state,
    dialog: shouldCloseDialog ? undefined : state.dialog,
    requestQueue
  }

  if (!shouldShow) {
    return [nextState, null]
  }

  return [
    nextState,
    Effect.gen(function* () {
      const snackbar = yield* Snackbar
      yield* snackbar.show(successMessage)
      return null
    })
  ]
}

// Search matches assignees by email; the rows carry only assignee ids, which
// resolve through the organization's users.
export function selFinalFolderList(
  state: STFolderListModuleStateLoaded,
  organizationUsers: BasicUser[]
): FolderListRow[] {
  const folders = Object.values(state.folderListState.folders)
  const filteredFolders = filterFolderList(
    folders,
    state.filters,
    state.folderListState.tags,
    organizationUsers
  )
  return sortFolderList(filteredFolders, state.sortState)
}

export function selOptimisticFolderAssigneeIds(
  state: STFolderListModuleState,
  folderId: string
): string[] {
  if (state.status != 'loaded') {
    return []
  }

  const folder = state.folderListState.folders[folderId]
  if (!folder) {
    return []
  }

  let assigneeIds = folder.assigneeIds
  for (const queueItem of state.requestQueue.items) {
    if (queueItem.status == 'finalized') {
      continue
    }
    const req = queueItem.op.request.request
    if (req.type != 'assignFolder' && req.type != 'unassignFolder') {
      continue
    }
    if (req.folderId != folderId) {
      continue
    }

    if (req.type == 'assignFolder') {
      if (!assigneeIds.includes(req.userId)) {
        assigneeIds = [...assigneeIds, req.userId]
      }
      continue
    }

    assigneeIds = assigneeIds.filter((id) => id != req.userId)
  }

  return assigneeIds
}

function folderSummarySearchIndex(
  item: FolderListRow,
  tags: FolderTag[],
  emailsByUserId: Map<string, string>
): string {
  const els = [item.name, ...item.clientEmails]
  if (item.sourceId) {
    els.push(item.sourceId)
  }
  for (const assigneeId of item.assigneeIds) {
    const email = emailsByUserId.get(assigneeId)
    if (email) {
      els.push(email)
    }
  }

  els.push(item.year.toString())
  els.push(item.type)

  for (const tid of item.tagIds) {
    const tag = tags.find((t) => t.id == tid)
    if (tag?.label) {
      els.push(tag?.label)
    }
  }

  if (item.archived) {
    els.push('archived')
  }

  return els.join(' ')
}

function filterFolderList(
  items: FolderListRow[],
  filter: STFolderListFilters,
  tags: FolderTag[],
  organizationUsers: BasicUser[]
): FolderListRow[] {
  var filteredItems: FolderListRow[] = items

  if (isNotEmpty(filter.keywords)) {
    const emailsByUserId = new Map(organizationUsers.map((user) => [user.id, user.email]))
    filteredItems = search(
      filteredItems,
      (el) => {
        const searchIndex = folderSummarySearchIndex(el, tags, emailsByUserId)
        return searchIndex
      },
      filter.keywords
    )
  }

  if (filter.year) {
    filteredItems = filteredItems.filter((el) => el.year == filter.year)
  }
  if (filter.type) {
    filteredItems = filteredItems.filter((el) => el.type == filter.type)
  }

  const assigneeId = filter.assigneeId
  if (assigneeId != undefined) {
    if (assigneeId == UNASSIGNED) {
      filteredItems = filteredItems.filter((el) => el.assigneeIds.length == 0)
    } else {
      filteredItems = filteredItems.filter((el) => el.assigneeIds.includes(assigneeId))
    }
  }

  const status = filter.status
  if (status) {
    filteredItems = filteredItems.filter((el) => filterByStatus(el, status))
  }

  // If we are not showing archived folders, we want to filter them out
  // Unless the user is searching for "archived", in which case let the
  // keyword filter (which matches against the search index) handle it.
  if (filter.showArchivedFolders == false && !/\barchived\b/i.test(filter.keywords)) {
    filteredItems = filteredItems.filter((el) => !el.archived)
  }

  return filteredItems
}

function sortFolderList(
  items: FolderListRow[],
  sortState: SortState<FolderListSortColumn> | undefined
): FolderListRow[] {
  sortState = sortState ?? FOLDER_DEFAULT_SORT

  switch (sortState.column) {
    case 'entities':
      return sort(
        items,
        levels(
          (a, b) => compareNames(sortState.direction, a.name, b.name),
          direction(sortState.direction, 'type')
        )
      )
    case 'createdAt':
      return sort(items, levels(direction(sortState.direction, 'createdAt')))
    case 'clientOrganizer.updatedAt':
      return sort(
        items,
        levels(
          direction(sortState.direction, (s) => s.clientOrganizer.updatedAt),
          direction(sortState.direction, 'createdAt')
        )
      )
    case 'year':
      return sort(items, levels(direction(sortState.direction, 'year')))
    case 'type':
      return sort(items, rank('type', RETURN_TYPES, sortState.direction))
    case 'clientOrganizer':
      return sort(
        items,
        levels(
          rank(
            (s) => s.clientOrganizer.status,
            ['not_seen_yet', 'in_progress', 'submitted'],
            sortState.direction
          ),
          direction(sortState.direction, (s) => s.clientOrganizer.questionnaireProgress ?? 0)
        )
      )
    case 'checklist':
      return sort(
        items,
        levels(
          direction(sortState.direction, (s) => {
            if (s.checklistItemsTotalCount == 0) return 0
            return s.checklistItemsCompleteCount / s.checklistItemsTotalCount
          }),
          direction(sortState.direction, 'checklistItemsTotalCount')
        )
      )
    case 'documents':
      return sort(
        items,
        levels(
          direction(sortState.direction, 'documentsCount'),
          direction(sortState.direction, (s) => s.clientOrganizer.updatedAt)
        )
      )
    case 'delivery':
      return sort(
        items,
        levels(
          rank(
            folderDeliveryStatus,
            ['not_delivered', 'delivered', 'in_progress', 'signed'],
            sortState.direction
          ),
          direction(sortState.direction, 'createdAt')
        )
      )
  }
}

function compareNames(direction: SortDirection, a: string, b: string): number {
  const value = compareName(a).localeCompare(compareName(b), 'en', {
    sensitivity: 'base'
  })
  return direction == 'asc' ? value : -value
}

function compareName(name: string): string {
  // push empty names to the bottom of the list
  return name == '' ? 'zzzzz' : name
}

export function folderDeliveryStatus(folder: FolderListRow): TaxReturnDeliveryStatus {
  return folder.clientOrganizer.deliveryStatus ?? 'not_delivered'
}

function filterByStatus(item: FolderListRow, status: FolderListStatusFilter): boolean {
  return match(status)
    .with({ kind: 'organizer' }, ({ status }) =>
      match(status)
        .with('not_seen_yet', () => item.clientOrganizer.status == 'not_seen_yet')
        .with(
          'not_seen_yet_not_sent',
          () => item.clientOrganizer.status == 'not_seen_yet' && !item.clientOrganizer.sent
        )
        .with(
          'not_seen_yet_sent',
          () => item.clientOrganizer.status == 'not_seen_yet' && item.clientOrganizer.sent
        )
        .with('in_progress', () => item.clientOrganizer.status == 'in_progress')
        .with('submitted', () => item.clientOrganizer.status == 'submitted')
        .exhaustive()
    )
    .with({ kind: 'delivery' }, ({ status }) => folderDeliveryStatus(item) == status)
    .exhaustive()
}

function selHasAssignmentConflict(
  state: STFolderListModuleState,
  folderIds: string[],
  userIds: string[]
): boolean {
  const candidates = new Set(userIds)
  return selFoldersById(state, folderIds).some((folder) =>
    folder.clientIds.some((clientId) => candidates.has(clientId))
  )
}

export function selFoldersById(
  state: STFolderListModuleState,
  folderIds: string[]
): FolderListRow[] {
  if (folderIds.length == 0) {
    return []
  }
  if (state.status != 'loaded') return []
  return folderIds.map((id) => state.folderListState.folders[id]).filter(isNotEmpty)
}

export function selBulkSendSelectedFolders(
  folderIds: string[],
  folders: FolderListRow[]
): BulkSendFolderGroups {
  const foldersById = new Map(folders.map((folder) => [folder.id, folder]))
  const selectedFolders = folderIds.map((id) => foldersById.get(id)).filter(isNotEmpty)

  const groups: BulkSendFolderGroups = {
    sendable: [],
    missingEmails: [],
    folders: selectedFolders
  }

  for (const folder of selectedFolders) {
    if (folder.clientEmails.length == 0) {
      groups.missingEmails.push(folder)
    } else {
      groups.sendable.push(folder)
    }
  }
  return groups
}

export function selAvailableBulkActions(state: STFolderListModuleState): BatchActionType[] {
  const folders = selFoldersById(state, state.selectedFolderIds)
  const actions: BatchActionType[] = []

  if (state.selectedFolderIds.length > 0) {
    actions.push({ type: 'assignFolders', group: 'folder', folderIds: state.selectedFolderIds })
  }

  // Bulk archive folders
  const unarchivedFolders = folders.filter((f) => !f.archived)
  if (unarchivedFolders.length > 0) {
    actions.push({
      type: 'archiveFolders',
      group: 'folder',
      folderIds: unarchivedFolders.map((f) => f.id)
    })
  }

  const archivedFolders = folders.filter((f) => f.archived)
  if (archivedFolders.length > 0) {
    actions.push({
      type: 'unarchiveFolders',
      group: 'folder',
      folderIds: archivedFolders.map((f) => f.id)
    })
  }

  actions.push({
    type: 'sendClientOrganizers',
    group: 'clientOrganizer',
    folderIds: state.selectedFolderIds
  })

  if (state.selectedFolderIds.length > 0) {
    actions.push({
      type: 'exportCsv',
      group: 'clientOrganizer',
      folderIds: state.selectedFolderIds
    })
  }

  const unenrolledFolders = folders.filter(
    (f) => f.clientOrganizer.status != 'submitted' && !f.clientOrganizer.reminderSchedule
  )
  if (unenrolledFolders.length > 0) {
    actions.push({
      type: 'enrollInReminders',
      group: 'clientOrganizer',
      folderIds: unenrolledFolders.map((f) => f.id)
    })
  }

  const enrolledFolders = folders.filter(
    (f) => f.clientOrganizer.status != 'submitted' && f.clientOrganizer.reminderSchedule
  )
  if (enrolledFolders.length > 0) {
    actions.push({
      type: 'unenrollFromReminders',
      group: 'clientOrganizer',
      folderIds: enrolledFolders.map((f) => f.id)
    })
  }

  if (state.selectedFolderIds.length > 0) {
    actions.push({
      type: 'addChecklistItems',
      group: 'folder',
      folderIds: state.selectedFolderIds
    })
  }

  actions.push({ type: 'deleteFolders', group: 'folder', folderIds: state.selectedFolderIds })

  return actions
}

const BULK_CALL_SDK_CONCURRENCY = 8

function callSdk(request: SDKCommand, requestId: string) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* callSdkRequest(sdk.send, request, {
      onError: (error) =>
        reportHandledError(error, {
          source: 'st-folder-list',
          operation: request.type,
          extra: { request, requestId }
        })
    })

    if (result.type == 'ok') {
      return { type: 'response', requestId } as const
    }
    if (result.type == 'unauthorized') {
      return { type: 'requestUnauthorized', requestId } as const
    }
    return { type: 'requestFailed', requestId } as const
  })
}

function callBatchSdk(requests: SDKCommand[], requestId: string) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const messages = yield* Effect.forEach(
      requests,
      (request) =>
        callSdkRequest(sdk.send, request, {
          onError: (error) =>
            reportHandledError(error, {
              source: 'st-folder-list',
              operation: request.type,
              extra: { request, requestId }
            })
        }).pipe(
          Effect.map((result) => {
            if (result.type == 'ok') {
              return { type: 'response' } as const
            }
            if (result.type == 'unauthorized') {
              return { type: 'requestUnauthorized' } as const
            }
            return { type: 'requestFailed' } as const
          })
        ),
      { concurrency: BULK_CALL_SDK_CONCURRENCY }
    )

    for (const message of messages) {
      if (message.type == 'requestUnauthorized') {
        return { type: 'requestUnauthorized', requestId } as const
      }
      if (message.type == 'requestFailed') {
        return { type: 'requestFailed', requestId } as const
      }
    }

    return { type: 'response', requestId } as const
  })
}

function csvExportKey(organizationId: string, folderIds: string[]) {
  return `${organizationId}:${folderIds.slice().sort().join(',')}`
}

export function bulkAddChecklistItemsDocTypesKey(schemaId: string, folderIds: string[]) {
  return `${schemaId}:${folderIds.slice().sort().join(',')}`
}

function loadBulkAddChecklistItemsDocTypesEffect({
  schemaId,
  folderIds,
  key
}: {
  schemaId: string
  folderIds: string[]
  key: string
}) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const request = {
      type: 'formConfig/getDocumentTypes',
      schemaId
    } satisfies SDKRequestFor<'formConfig/getDocumentTypes'>
    const result = yield* runSdkRequest(sdk.send(request), {
      onError: (error) =>
        reportHandledError(error, {
          source: 'st-folder-list',
          operation: request.type,
          extra: { request, key, folderIds }
        })
    })

    if (result.ok) {
      return {
        type: 'bulkAddChecklistItemsDocTypes',
        message: {
          type: 'loaded',
          documentTypes: result.response.map((documentType) => ({
            id: documentType.id,
            name: documentType.name
          }))
        }
      } as const
    }
    return {
      type: 'bulkAddChecklistItemsDocTypes',
      message: { type: 'failed' }
    } as const
  })
}

function loadCsvExportEffect({
  organizationId,
  folderIds,
  key
}: {
  organizationId: string
  folderIds: string[]
  key: string
}) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const request = {
      type: 'folders/getFolderListExport',
      organizationId,
      folderIds
    } satisfies SDKRequestFor<'folders/getFolderListExport'>
    const result = yield* runSdkRequest(sdk.send(request), {
      onError: (error) =>
        reportHandledError(error, {
          source: 'st-folder-list',
          operation: request.type,
          extra: { request, key }
        })
    })

    if (result.ok) {
      return {
        type: 'csvExport',
        message: { type: 'loaded', data: result.response }
      } as const
    }
    return {
      type: 'csvExport',
      message: { type: 'failed' }
    } as const
  })
}
